import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader, Carousel, CarouselItem, CarouselControl, CarouselIndicators, CarouselCaption, Spinner } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import {
  getSuiteInspectionAttachmentSignedUrls,
  getSuiteInspectionHistory,
  requestSuiteInspectionUploadUrl,
  confirmSuiteInspectionUpload,
} from './FormikHelpers/Api';
import { InspectionFormModal } from './InspectionFormModal';
import { transformInspectionForForm } from './FormikHelpers/InitialValues';

const LS_KEY = 'si_table_col_widths';
const LS_VIEW_MODE_KEY = 'si_view_mode';

const FACTORY_WIDTHS = {
  expand: 44, inspection_date: 132, unit_number: 104, cleanliness: 136,
  any_damages: 114, number_of_dogs: 90, number_of_cats: 90, plumbing: 114, dye_test: 114,
  closed_window: 156, furniture_away: 164, hrv_setting: 132,
  furnace_filter: 144, flooring_damage: 136, lubricate_dead_bolt: 124,
  number_of_people: 94, signs_of_smoking: 114, overall_status: 132,
  attachments: 134, comments: 260, actions: 140,
};

const COL_LABELS = {
  expand: '(icon)', inspection_date: 'Date', unit_number: 'Unit #',
  cleanliness: 'Cleanliness', any_damages: 'Damages', number_of_dogs: 'Dogs', number_of_cats: 'Cats',
  plumbing: 'Plumbing', dye_test: 'Dye Test', closed_window: 'Closed Window',
  furniture_away: 'Furniture Away', hrv_setting: 'HRV Setting',
  furnace_filter: 'Furnace Filter', flooring_damage: 'Flooring Dmg',
  lubricate_dead_bolt: 'Dead Bolt', number_of_people: 'People',
  signs_of_smoking: 'Smoking', overall_status: 'Status',
  attachments: 'Files', comments: 'Comments', actions: '(actions)',
};

const FIELD_LABELS = {
  unit_number: 'Unit #', cleanliness: 'Cleanliness', any_damages: 'Damages',
  number_of_dogs: 'Dogs', number_of_cats: 'Cats',
  plumbing: 'Plumbing', dye_test: 'Dye Test',
  lubricate_dead_bolt: 'Dead Bolt', number_of_people: 'People',
  signs_of_smoking: 'Smoking', comments: 'Comments', overall_status: 'Status',
  closed_window_winters: 'Closed Window', furniture_away_heating_pipes: 'Furniture Away',
  hrv_setting: 'HRV Setting', furnace_filter: 'Furnace Filter',
  flooring_damage: 'Flooring Dmg', inspection_date: 'Date', attachments: 'Attachments',
};

const cap = str => {
  if (str === null || str === undefined || str === '') return '—';
  const s = String(str).trim();
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const loadSavedWidths = () => {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? { ...FACTORY_WIDTHS, ...JSON.parse(raw) } : { ...FACTORY_WIDTHS }; }
  catch { return { ...FACTORY_WIDTHS }; }
};

const saveWidths = widths => { try { localStorage.setItem(LS_KEY, JSON.stringify(widths)); } catch { } };

const loadViewMode = () => {
  try { return localStorage.getItem(LS_VIEW_MODE_KEY) || 'inline'; }
  catch { return 'inline'; }
};

const saveViewMode = mode => { try { localStorage.setItem(LS_VIEW_MODE_KEY, mode); } catch { } };

const fmtDate = d => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const fmtDateTime = d => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ status, isDraft }) => {
  if (isDraft) return <span className="si-badge si-badge--draft"><FeatherIcon icon="edit-3" size={10} /> Draft</span>;
  if (status === 'has issues') return <span className="si-badge si-badge--issues"><FeatherIcon icon="alert-circle" size={10} /> Need Attention</span>;
  return <span className="si-badge si-badge--passed"><FeatherIcon icon="check-circle" size={10} /> Completed</span>;
};

const BoolBadge = ({ value }) => {
  if (value === true || value === 'yes') return <span className="si-badge si-badge--yes"><FeatherIcon icon="alert-circle" size={10} /> Yes</span>;
  if (value === false || value === 'no') return <span className="si-badge si-badge--no"> <FeatherIcon icon="check" size={10} /> No</span>;
  if (value === 'na') return <span className="si-badge si-badge--na">N/A</span>;
  return <span className="si-cell-muted">—</span>;
};

const CleanBadge = ({ val }) => {
  if (!val) return <span className="si-cell-muted">—</span>;
  const map = { clean: 'si-badge--clean', okay: 'si-badge--okay', dirty: 'si-badge--dirty', 'very dirty': 'si-badge--very-dirty' };
  return <span className={`si-badge ${map[val] || ''}`}>{cap(val)}</span>;
};

const PetCountCell = ({ count, emoji }) => {
  if (!count && count !== 0) return <span className="si-cell-muted">—</span>;
  const n = parseInt(count, 10) || 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: '13px', color: n > 0 ? '#6366F1' : '#94A3B8' }}>
      <span style={{ fontSize: '14px' }}>{emoji}</span>{n}
    </span>
  );
};

const cellVal = (row, key) => {
  switch (key) {
    case 'inspection_date': return <span className="si-cell-date">{fmtDate(row.inspection_date)}</span>;
    case 'cleanliness': return <CleanBadge val={row.cleanliness} />;
    case 'any_damages': return <BoolBadge value={row.any_damages} />;
    case 'number_of_dogs': return <PetCountCell count={row.number_of_dogs} emoji="🐶" />;
    case 'number_of_cats': return <PetCountCell count={row.number_of_cats} emoji="🐱" />;
    case 'plumbing':
      if (!row.plumbing) return <span className="si-cell-muted">—</span>;
      return <span className={`si-badge ${row.plumbing === 'no_issue' ? 'si-badge--plumbing-good' : 'si-badge--plumbing-issue'}`}><FeatherIcon icon={row.plumbing === 'no_issue' ? 'thumbs-up' : 'alert-triangle'} size={10} />{row.plumbing === 'no_issue' ? 'No Issue' : 'Needs Repair'}</span>;
    case 'dye_test':
      if (!row.dye_test) return <span className="si-cell-muted">—</span>;
      return <span className={`si-badge ${row.dye_test === 'passed' ? 'si-badge--passed' : 'si-badge--issues'}`}><FeatherIcon icon={row.dye_test === 'passed' ? 'check' : 'x'} size={10} />{cap(row.dye_test)}</span>;
    case 'closed_window': case 'furniture_away': case 'flooring_damage': return <BoolBadge value={row[key]} />;
    case 'hrv_setting': if (!row.hrv_setting) return <span className="si-cell-muted">—</span>; return <span className="si-badge si-badge--hrv">{cap(row.hrv_setting)}</span>;
    case 'furnace_filter': if (!row.furnace_filter) return <span className="si-cell-muted">—</span>; return <span className={`si-badge ${row.furnace_filter === 'clean' ? 'si-badge--furnace-clean' : 'si-badge--furnace-replaced'}`}>{cap(row.furnace_filter)}</span>;
    case 'lubricate_dead_bolt':
      if (!row.lubricate_dead_bolt) return <span className="si-cell-muted">—</span>;
      return <span className={`si-badge ${row.lubricate_dead_bolt === 'no_issue' ? 'si-badge--done' : 'si-badge--issues'}`}>{row.lubricate_dead_bolt === 'no_issue' && <FeatherIcon icon="check" size={10} />}{row.lubricate_dead_bolt === 'no_issue' ? 'No Issue' : row.lubricate_dead_bolt === 'needs_repair' ? 'Needs Repair' : row.lubricate_dead_bolt || '—'}</span>;
    case 'number_of_people': return row.number_of_people ? <span className="si-cell-number">{row.number_of_people}</span> : <span className="si-cell-muted">—</span>;
    case 'signs_of_smoking': return <BoolBadge value={row.signs_of_smoking} />;
    case 'overall_status': return <StatusBadge status={row.overall_status} isDraft={row.is_draft} />;
    case 'unit_number': return <span className="si-cell-unit">{row.unit_number || '—'}</span>;
    case 'attachments': { const n = (row.attachments || []).length; return n > 0 ? <span className="si-badge si-badge--files"><FeatherIcon icon="paperclip" size={10} /> {n} file{n !== 1 ? 's' : ''}</span> : <span className="si-cell-muted">—</span>; }
    case 'comments': return <Tooltip title={row.comments || '—'} arrow placement="top"><span className={`si-cell-comment ${row.comments ? '' : 'si-cell-muted'}`}>{row.comments ? cap(row.comments) : '—'}</span></Tooltip>;
    default: return <span className="si-cell-text">{row[key] ?? '—'}</span>;
  }
};

const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false); const startX = useRef(0);
  const onDown = e => {
    e.preventDefault(); dragging.current = true; startX.current = e.clientX;
    const onMove = ev => { if (dragging.current) { onResize(ev.clientX - startX.current); startX.current = ev.clientX; } };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };
  return <div className="si-resize-handle" onMouseDown={onDown}><div className="si-rhandle" /></div>;
};

const formatHistoryValue = (field, value) => {
  if (value === null || value === undefined || value === '') return <span className="si-history-change__empty">empty</span>;
  if (field === 'inspection_date') return fmtDate(value);
  if (field === 'attachments') return Array.isArray(value) ? `${value.length} file(s)` : String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return cap(String(value));
};

const HistoryPopover = ({ inspectionId, unitNumber, anchorEl, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(0);
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' });
  useEffect(() => {
    if (!anchorEl || !inspectionId) return;
    setLoading(true); setError(null); setExpandedIdx(0);
    getSuiteInspectionHistory(inspectionId)
      .then(res => { setHistory(res?.data || []); setLoading(false); })
      .catch(() => { setError('Failed to load history'); setLoading(false); });
  }, [inspectionId, anchorEl]);
  useEffect(() => {
    if (!anchorEl || !popoverRef.current) return;
    const rect = anchorEl.getBoundingClientRect();
    const popW = 420; const popH = 380;
    const vp = { w: window.innerWidth, h: window.innerHeight };
    let left = Math.max(8, Math.min(rect.left + rect.width / 2 - popW / 2, vp.w - popW - 8));
    const spaceBelow = vp.h - rect.bottom; const spaceAbove = rect.top;
    let top, placement;
    if (spaceBelow >= popH + 12 || spaceBelow >= spaceAbove) { top = rect.bottom + window.scrollY + 8; placement = 'bottom'; }
    else { top = rect.top + window.scrollY - popH - 8; placement = 'top'; }
    setPosition({ top, left, placement });
  }, [anchorEl]);
  useEffect(() => {
    const handler = e => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorEl, onClose]);
  if (!anchorEl) return null;
  return (
    <div ref={popoverRef} className={`si-history-popover si-history-popover--${position.placement}`} style={{ top: position.top, left: position.left }} onClick={e => e.stopPropagation()}>
      <div className="si-history-popover__arrow" />
      <div className="si-history-popover__header">
        <div className="si-history-popover__header-icon"><FeatherIcon icon="clock" size={13} color="#93C5FD" /></div>
        <div className="si-history-popover__header-info">
          <span className="si-history-popover__header-label">Change History</span>
          <span className="si-history-popover__header-unit">Unit {unitNumber}</span>
        </div>
        {!loading && !error && <span className="si-history-popover__count">{history.length} event{history.length !== 1 ? 's' : ''}</span>}
        <button type="button" className="si-icon-btn si-icon-btn--sm" onClick={onClose}><FeatherIcon icon="x" size={12} color="#94A3B8" /></button>
      </div>
      <div className="si-history-popover__body">
        {loading && <div className="si-history-popover__state"><Spinner size="sm" className="si-spinner--blue" /><span>Loading history…</span></div>}
        {error && !loading && <div className="si-history-popover__state si-history-popover__state--error"><FeatherIcon icon="alert-circle" size={14} /><span>{error}</span></div>}
        {!loading && !error && history.length === 0 && <div className="si-history-popover__state si-history-popover__state--empty"><FeatherIcon icon="inbox" size={22} color="#CBD5E1" /><span>No changes recorded yet</span></div>}
        {!loading && !error && history.length > 0 && (
          <div className="si-history-timeline">
            {history.map((entry, idx) => (
              <div key={entry._id || idx} className={`si-history-entry${idx === expandedIdx ? ' si-history-entry--open' : ''}`}>
                <div className="si-history-entry__track">
                  <div className={`si-history-entry__dot${idx === 0 ? ' si-history-entry__dot--latest' : ''}`} />
                  {idx < history.length - 1 && <div className="si-history-entry__line" />}
                </div>
                <div className="si-history-entry__content">
                  <button type="button" className="si-history-entry__header" onClick={() => setExpandedIdx(idx === expandedIdx ? -1 : idx)}>
                    <div className="si-history-entry__meta">
                      <span className="si-history-entry__who"><FeatherIcon icon="user" size={10} />{entry.changed_by_name || 'Unknown'}</span>
                      <span className="si-history-entry__when">{fmtDateTime(entry.changed_at)}</span>
                    </div>
                    <div className="si-history-entry__summary">
                      <span className="si-history-entry__field-count">{entry.changes?.length || 0} field{(entry.changes?.length || 0) !== 1 ? 's' : ''}</span>
                      <FeatherIcon icon={idx === expandedIdx ? 'chevron-up' : 'chevron-down'} size={11} color="#94A3B8" />
                    </div>
                  </button>
                  {idx === expandedIdx && entry.changes?.length > 0 && (
                    <div className="si-history-entry__changes">
                      {entry.changes.map((change, ci) => (
                        <div key={ci} className="si-history-change">
                          <span className="si-history-change__field">{FIELD_LABELS[change.field] || cap(change.field)}</span>
                          <div className="si-history-change__values">
                            <span className="si-history-change__old">{formatHistoryValue(change.field, change.old_value)}</span>
                            <FeatherIcon icon="arrow-right" size={10} color="#94A3B8" />
                            <span className="si-history-change__new">{formatHistoryValue(change.field, change.new_value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {idx === expandedIdx && (!entry.changes || entry.changes.length === 0) && <div className="si-history-entry__no-detail">No field details recorded</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const HistoryTriggerButton = ({ row, onOpen }) => {
  const btnRef = useRef(null);
  const hasHistory = row.history && row.history.length > 0;
  return (
    <Tooltip title="View change history" arrow placement="top">
      <button ref={btnRef} type="button" className={`si-history-btn${hasHistory ? ' si-history-btn--has-history' : ''}`} onClick={e => { e.stopPropagation(); onOpen(btnRef.current, row._id, row.unit_number); }}>
        <FeatherIcon icon="clock" size={11} />
        {hasHistory && <span className="si-history-btn__count">{row.history.length}</span>}
      </button>
    </Tooltip>
  );
};

const ColWidthSettingsPanel = ({ colWidths, onApply, onClose, isApt }) => {
  const [draft, setDraft] = useState({ ...colWidths });
  const allKeys = Object.keys(FACTORY_WIDTHS).filter(k => {
    if (!isApt && (k === 'closed_window' || k === 'furniture_away')) return false;
    if (isApt && (k === 'hrv_setting' || k === 'furnace_filter' || k === 'flooring_damage')) return false;
    return true;
  });
  const set = (k, v) => { const n = parseInt(v, 10); setDraft(prev => ({ ...prev, [k]: isNaN(n) ? prev[k] : Math.max(40, Math.min(600, n)) })); };
  const groups = [
    { label: 'Core', keys: ['expand', 'inspection_date', 'unit_number', 'actions'] },
    { label: 'Unit Details', keys: ['cleanliness', 'any_damages', 'number_of_dogs', 'number_of_cats', 'plumbing', 'dye_test'] },
    { label: isApt ? 'Apartment Checks' : 'Townhouse Checks', keys: isApt ? ['closed_window', 'furniture_away'] : ['hrv_setting', 'furnace_filter', 'flooring_damage'] },
    { label: 'Shared Checks', keys: ['lubricate_dead_bolt', 'number_of_people', 'signs_of_smoking'] },
    { label: 'Summary', keys: ['overall_status', 'attachments', 'comments'] },
  ];
  return (
    <div className="si-settings-panel">
      <div className="si-settings-panel__header">
        <div className="si-settings-panel__header-left">
          <div className="si-settings-panel__header-icon"><FeatherIcon icon="sliders" size={14} color="#93C5FD" /></div>
          <div><div className="si-settings-panel__header-label">Column Settings</div><div className="si-settings-panel__header-title">Adjust Default Widths</div></div>
        </div>
        <button className="si-icon-btn" onClick={onClose} type="button"><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>
      <div className="si-settings-panel__body">
        <div className="si-settings-panel__info-banner"><FeatherIcon icon="info" size={12} color="#1D4ED8" />Drag column header handles for one-time adjustments. Set values here to change saved defaults.</div>
        {groups.map(group => (
          <div key={group.label} className="si-settings-panel__group">
            <div className="si-settings-panel__group-label">{group.label}</div>
            <div className="si-settings-panel__col-grid">
              {group.keys.filter(k => allKeys.includes(k)).map(k => (
                <div key={k} className="si-settings-panel__col-row">
                  <span className="si-settings-panel__col-label">{COL_LABELS[k]}</span>
                  <div className="si-settings-panel__stepper">
                    <button type="button" className="si-settings-panel__stepper-btn" onClick={() => set(k, (draft[k] || FACTORY_WIDTHS[k]) - 10)}>−</button>
                    <input type="number" className="si-settings-panel__stepper-input" value={draft[k] || FACTORY_WIDTHS[k]} onChange={e => set(k, e.target.value)} min={40} max={600} />
                    <button type="button" className="si-settings-panel__stepper-btn" onClick={() => set(k, (draft[k] || FACTORY_WIDTHS[k]) + 10)}>+</button>
                    <span className="si-settings-panel__stepper-unit">px</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="si-settings-panel__footer">
        <button type="button" className="si-btn si-btn--ghost" onClick={() => setDraft({ ...FACTORY_WIDTHS })}><FeatherIcon icon="rotate-ccw" size={12} /> Reset to Factory</button>
        <div className="si-settings-panel__footer-actions">
          <button type="button" className="si-btn si-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="si-btn si-btn--primary" onClick={() => { onApply(draft); onClose(); }}><FeatherIcon icon="save" size={13} /> Save & Apply</button>
        </div>
      </div>
    </div>
  );
};

const MediaCarouselModal = ({ isOpen, toggle, items, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  useEffect(() => { if (isOpen) setActiveIndex(initialIndex); }, [isOpen, initialIndex]);
  const next = () => { if (!animating) setActiveIndex(i => (i === items.length - 1 ? 0 : i + 1)); };
  const prev = () => { if (!animating) setActiveIndex(i => (i === 0 ? items.length - 1 : i - 1)); };
  if (!isOpen || !items?.length) return null;
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" className="si-carousel-modal">
      <div className="si-carousel-modal__header">
        <div className="si-carousel-modal__header-left"><div className="si-carousel-modal__icon"><FeatherIcon icon="film" size={15} color="white" /></div><span className="si-carousel-modal__title">Media Preview</span><span className="si-carousel-modal__count">{items.length} item{items.length !== 1 ? 's' : ''}</span></div>
        <button type="button" className="si-icon-btn" onClick={toggle}><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>
      <ModalBody className="si-carousel-modal__body p-0">
        <Carousel activeIndex={activeIndex} next={next} previous={prev} pause={false} interval={false}>
          <CarouselIndicators items={items} activeIndex={activeIndex} onClickHandler={i => { if (!animating) setActiveIndex(i); }} />
          {items.map((item, idx) => (
            <CarouselItem onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)} key={idx}>
              <div className="si-carousel-modal__slide">{item.type === 'video' ? <video src={item.src} controls autoPlay className="si-carousel-modal__media" /> : <img src={item.src} alt={item.caption} className="si-carousel-modal__media" />}</div>
              <CarouselCaption captionText={item.caption} className="si-carousel-modal__caption" />
            </CarouselItem>
          ))}
          <CarouselControl direction="prev" directionText="Prev" onClickHandler={prev} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </Carousel>
      </ModalBody>
      <div className="si-carousel-modal__footer"><button type="button" className="si-btn si-btn--ghost-light" onClick={toggle}>Close</button></div>
    </Modal>
  );
};

const AttachmentViewCell = ({ row }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [carouselOpen, setCarouselOpen] = useState(false);
  const fetchedRef = useRef(new Set());
  const attachments = row.attachments || [];
  useEffect(() => {
    const missing = attachments.filter(k => !signedUrls[k] && !fetchedRef.current.has(k));
    if (!missing.length) return;
    missing.forEach(k => fetchedRef.current.add(k));
    getSuiteInspectionAttachmentSignedUrls(missing)
      .then(urls => setSignedUrls(prev => ({ ...prev, ...urls })))
      .catch(() => { });
  }, [attachments]);
  const carouselItems = useMemo(() => attachments.map((k, i) => ({
    src: signedUrls[k] || '',
    caption: `Media ${i + 1}`,
    type: /\.(mp4|mov|webm|avi|mkv)$/i.test(k) ? 'video' : 'image',
  })), [attachments, signedUrls]);
  if (attachments.length === 0) return <span className="si-cell-muted">—</span>;
  return (
    <>
      <button type="button" className="si-attachment__view-btn" onClick={e => { e.stopPropagation(); setCarouselOpen(true); }}>
        <FeatherIcon icon="image" size={10} /> {attachments.length} file{attachments.length !== 1 ? 's' : ''}
      </button>
      <MediaCarouselModal isOpen={carouselOpen} toggle={() => setCarouselOpen(false)} items={carouselItems} initialIndex={0} />
    </>
  );
};

const doUploadFiles = async (inspectionId, pendingFiles) => {
  const keys = [];
  for (const { file, name } of pendingFiles) {
    try {
      const { presignedUrl, key } = await requestSuiteInspectionUploadUrl(inspectionId, name, file.type);
      await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      await confirmSuiteInspectionUpload(inspectionId, key);
      keys.push(key);
    } catch (err) {
      console.error('Upload failed:', name, err);
    }
  }
  return keys;
};

const INLINE_MAX_FILES = 10;
const INLINE_MAX_SIZE = 120 * 1024 * 1024;
const INLINE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska';

const InlineAttachmentCell = ({ existingKeys, pendingFiles, onPendingChange, onExistingChange }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const fetchedRef = useRef(new Set());
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const missing = existingKeys.filter(k => !signedUrls[k] && !fetchedRef.current.has(k));
    if (!missing.length) return;
    missing.forEach(k => fetchedRef.current.add(k));
    getSuiteInspectionAttachmentSignedUrls(missing)
      .then(urls => setSignedUrls(prev => ({ ...prev, ...urls })))
      .catch(() => { });
  }, [existingKeys]);

  useEffect(() => () => pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview)), []);

  const addFiles = (accepted, rejected) => {
    rejected.forEach(({ file, errors }) => errors.forEach(e => {
      if (e.code === 'file-too-large') toast.error(`${file.name} exceeds 120 MB`);
      if (e.code === 'file-invalid-type') toast.error(`${file.name}: unsupported type`);
    }));
    const rem = INLINE_MAX_FILES - (pendingFiles.length + existingKeys.length);
    const toAdd = accepted.slice(0, rem);
    if (accepted.length > rem) toast.warn(`Max ${INLINE_MAX_FILES} files`);
    if (!toAdd.length) return;
    onPendingChange([...pendingFiles, ...toAdd.map(f => ({
      file: f, name: f.name, type: f.type,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }))]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    accept: INLINE_ACCEPT,
    multiple: true,
    maxSize: INLINE_MAX_SIZE,
    noClick: true, 
  });

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = [], rejected = [];
    files.forEach(f => {
      if (f.size > INLINE_MAX_SIZE) rejected.push({ file: f, errors: [{ code: 'file-too-large' }] });
      else valid.push(f);
    });
    addFiles(valid, rejected);
    e.target.value = '';
  };

  const handleCameraChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = [], rejected = [];
    files.forEach(f => {
      if (f.size > INLINE_MAX_SIZE) rejected.push({ file: f, errors: [{ code: 'file-too-large' }] });
      else valid.push(f);
    });
    addFiles(valid, rejected);
    e.target.value = '';
  };

  const removePending = (i) => onPendingChange(pendingFiles.filter((_, idx) => idx !== i));
  const removeExisting = (i) => onExistingChange(existingKeys.filter((_, idx) => idx !== i));
  const total = pendingFiles.length + existingKeys.length;
  const atLimit = total >= INLINE_MAX_FILES;

  return (
    <div style={{ minWidth: 150 }} onClick={e => e.stopPropagation()}>
      {/* Hidden inputs outside dropzone to avoid event conflicts */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={INLINE_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={handleGalleryChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraChange}
      />

      <div
        style={{
          border: `1.5px dashed ${isDragActive ? '#6366F1' : '#CBD5E1'}`,
          borderRadius: 8,
          padding: '5px 6px',
          background: isDragActive ? '#EEF2FF' : '#FAFBFF',
          transition: 'all 0.2s',
          marginBottom: total > 0 ? 5 : 0,
          position: 'relative'
        }}
      >
        {/* Drop zone layer */}
        <div 
          {...getRootProps()} 
          style={{ 
            position: 'absolute', inset: 0, zIndex: isDragActive ? 10 : 0,
            cursor: isDragActive ? 'copy' : 'default'
          }}
        >
          <input {...getInputProps()} />
        </div>

        {/* Content layer */}
        <div style={{ position: 'relative', zIndex: 5, pointerEvents: 'none' }}>
          {isDragActive ? (
            <div style={{ textAlign: 'center', fontSize: '10px', color: '#6366F1', fontWeight: 600, padding: '2px 0' }}>
              Drop here
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 4, pointerEvents: 'auto' }}>
                <Tooltip title={/Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'Choose from gallery' : 'Upload media'} arrow placement="top">
                  <button
                    type="button"
                    disabled={atLimit}
                    onClick={() => galleryInputRef.current?.click()}
                    style={{
                      flex: 1,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                      padding: '4px 6px', borderRadius: 6,
                      border: `1px solid ${atLimit ? '#E2E8F0' : '#6366F1'}`,
                      background: atLimit ? '#F8FAFC' : '#EEF2FF',
                      color: atLimit ? '#CBD5E1' : '#6366F1',
                      fontSize: '10px', fontWeight: 700,
                      cursor: atLimit ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FeatherIcon icon="image" size={10} />
                    {/Mobi|Android|iPhone/i.test(navigator.userAgent) ? 'Gallery' : 'Media'}
                  </button>
                </Tooltip>

                {/Mobi|Android|iPhone/i.test(navigator.userAgent) && (
                  <Tooltip title="Take photo or video" arrow placement="top">
                    <button
                      type="button"
                      disabled={atLimit}
                      onClick={() => cameraInputRef.current?.click()}
                      style={{
                        flex: 1,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                        padding: '4px 6px', borderRadius: 6,
                        border: `1px solid ${atLimit ? '#E2E8F0' : '#10B981'}`,
                        background: atLimit ? '#F8FAFC' : '#ECFDF5',
                        color: atLimit ? '#CBD5E1' : '#059669',
                        fontSize: '10px', fontWeight: 700,
                        cursor: atLimit ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <FeatherIcon icon="camera" size={10} />
                      Camera
                    </button>
                  </Tooltip>
                )}
              </div>
              <div style={{ textAlign: 'center', fontSize: '9px', color: '#CBD5E1', marginTop: 3 }}>
                {atLimit ? `Limit reached (${INLINE_MAX_FILES})` : `${total}/${INLINE_MAX_FILES} • drag or use buttons`}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Thumbnails */}
      {total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {existingKeys.map((k, i) => {
            const url = signedUrls[k];
            const isVid = /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
            return (
              <div key={`ex-${i}`} style={{ position: 'relative', width: 36, height: 36, borderRadius: 6, overflow: 'hidden', border: '1.5px solid #E2E8F0', flexShrink: 0 }}>
                {url
                  ? isVid
                    ? <div style={{ width: '100%', height: '100%', background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FeatherIcon icon="video" size={14} color="#94A3B8" /></div>
                    : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size="sm" style={{ width: 10, height: 10 }} /></div>
                }
                <button type="button" onClick={() => removeExisting(i)} style={{ position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%', background: '#EF4444', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                  <FeatherIcon icon="x" size={8} color="white" />
                </button>
              </div>
            );
          })}
          {pendingFiles.map((f, i) => (
            <div key={`p-${i}`} style={{ position: 'relative', width: 36, height: 36, borderRadius: 6, overflow: 'hidden', border: '1.5px dashed #F59E0B', flexShrink: 0 }}>
              {f.type.startsWith('image/') && f.preview
                ? <img src={f.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FeatherIcon icon="video" size={14} color="#94A3B8" /></div>
              }
              <button type="button" onClick={() => removePending(i)} style={{ position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%', background: '#EF4444', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                <FeatherIcon icon="x" size={8} color="white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div style={{ fontSize: '9px', color: '#F59E0B', marginTop: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
          <FeatherIcon icon="clock" size={9} color="#F59E0B" />
          {pendingFiles.length} pending upload
        </div>
      )}
    </div>
  );
};

const HistoricInspectionsModal = ({ isOpen, onClose, unitNumber, inspections, onEdit, canEdit }) => {
  const sorted = useMemo(() => [...inspections].sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date)), [inspections]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = sorted[selectedIdx];
  useEffect(() => { if (isOpen) setSelectedIdx(0); }, [isOpen]);
  const DetailRow = ({ label, value }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '1px solid #F1F5F9' }}>
      <span style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.4px', minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '12px', color: '#0F172A', fontWeight: 500 }}>{value}</span>
    </div>
  );
  const boolLabel = v => v === true ? 'Yes' : v === false ? 'No' : v || '—';
  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered className="si-form-modal" style={{ maxWidth: 960 }}>
      <div style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #1E293B 100%)',
        padding: '20px 24px',
        borderRadius: '12px 12px 0 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FeatherIcon icon="archive" size={20} color="#A5B4FC" />
          </div>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(165,180,252,0.7)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 2 }}>Inspection Archive</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#F8FAFC' }}>Unit {unitNumber} — All Inspections ({sorted.length})</div>
          </div>
        </div>
        <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <FeatherIcon icon="x" size={15} color="white" />
        </button>
      </div>
      <ModalBody style={{ padding: 0, background: '#F8FAFC', display: 'flex', minHeight: 500 }}>
        <div style={{ width: 220, borderRight: '1px solid #E2E8F0', background: 'white', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '12px 14px', fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #F1F5F9' }}>Select Inspection</div>
          {sorted.map((insp, idx) => (
            <button key={insp._id} type="button" onClick={() => setSelectedIdx(idx)} style={{ width: '100%', textAlign: 'left', padding: '12px 14px', background: idx === selectedIdx ? '#EEF2FF' : 'transparent', border: 'none', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', borderLeft: idx === selectedIdx ? '3px solid #6366F1' : '3px solid transparent', transition: 'all 0.15s' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: idx === selectedIdx ? '#4F46E5' : '#0F172A', marginBottom: 3 }}>{fmtDate(insp.inspection_date)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {insp.is_draft ? <span style={{ fontSize: '10px', fontWeight: 700, color: '#D97706', background: '#FEF3C7', padding: '1px 7px', borderRadius: 20, border: '1px solid #FCD34D' }}>Draft</span>
                  : insp.overall_status === 'has issues' ? <span style={{ fontSize: '10px', fontWeight: 700, color: '#EF4444', background: '#FEE2E2', padding: '1px 7px', borderRadius: 20 }}>Need Attention</span>
                    : <span style={{ fontSize: '10px', fontWeight: 700, color: '#10B981', background: '#D1FAE5', padding: '1px 7px', borderRadius: 20 }}>Completed</span>}
                {idx === 0 && <span style={{ fontSize: '9px', fontWeight: 700, color: '#6366F1', background: '#EEF2FF', padding: '1px 6px', borderRadius: 20 }}>Latest</span>}
              </div>
              {insp.inspectedByName && <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: 3 }}>by {insp.inspectedByName}</div>}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {selected ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0F172A' }}>
                    Inspection — {fmtDate(selected.inspection_date)}
                    {selected.is_draft && <span style={{ marginLeft: 8, fontSize: '11px', fontWeight: 700, color: '#D97706', background: '#FEF3C7', padding: '2px 8px', borderRadius: 20, border: '1px solid #FCD34D' }}>Draft</span>}
                  </div>
                  {selected.inspectedByName && <div style={{ fontSize: '12px', color: '#64748B', marginTop: 2 }}>Inspected by {selected.inspectedByName}</div>}
                </div>
                {canEdit && <button type="button" onClick={() => { onClose(); onEdit(selected); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', border: 'none', color: 'white', fontSize: '12px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 3px 10px rgba(99,102,241,0.3)' }}><FeatherIcon icon="edit-2" size={13} /> Edit This Inspection</button>}
              </div>
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E2E8F0', padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><FeatherIcon icon="clipboard" size={13} color="#6366F1" /> General Information</div>
                <DetailRow label="Unit Number" value={selected.unit_number || '—'} />
                <DetailRow label="Type" value={cap(selected.type)} />
                <DetailRow label="Cleanliness" value={cap(selected.cleanliness)} />
                <DetailRow label="Any Damages" value={boolLabel(selected.any_damages)} />
                <DetailRow label="Signs of Smoking" value={boolLabel(selected.signs_of_smoking)} />
                <DetailRow label="Dogs" value={`🐶 ${selected.number_of_dogs ?? 0}`} />
                <DetailRow label="Cats" value={`🐱 ${selected.number_of_cats ?? 0}`} />
                <DetailRow label="Number of People" value={selected.number_of_people || '—'} />
                <DetailRow label="Plumbing" value={cap(selected.plumbing)} />
                <DetailRow label="Dye Test" value={cap(selected.dye_test)} />
                <DetailRow label="Dead Bolt Lubrication" value={cap(selected.lubricate_dead_bolt)} />
              </div>
              <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E2E8F0', padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><FeatherIcon icon="home" size={13} color="#8B5CF6" /> {cap(selected.type)} Specific</div>
                {selected.type === 'apartment' ? (
                  <><DetailRow label="Closed Window (Winters)" value={cap(selected.closed_window_winters)} /><DetailRow label="Furniture Away from Heating" value={cap(selected.furniture_away_heating_pipes)} /></>
                ) : (
                  <><DetailRow label="HRV Setting" value={cap(selected.hrv_setting)} /><DetailRow label="Furnace Filter" value={cap(selected.furnace_filter)} /><DetailRow label="Flooring Damage" value={boolLabel(selected.flooring_damage)} /></>
                )}
                <DetailRow label="Overall Status" value={selected.is_draft ? 'Draft (Incomplete)' : cap(selected.overall_status)} />
              </div>
              {selected.comments && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><FeatherIcon icon="message-square" size={12} color="#D97706" /> Comments</div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#78350F', lineHeight: 1.6 }}>{selected.comments}</p>
                </div>
              )}
              {selected.attachments?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E2E8F0', padding: '14px 18px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FeatherIcon icon="paperclip" size={12} color="#F59E0B" /> Attachments ({selected.attachments.length})</div>
                  <AttachmentViewCell row={selected} />
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8', fontSize: '13px' }}>Select an inspection from the list</div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

const CLEANLINESS_OPTS = ['clean', 'okay', 'dirty'];
const PLUMBING_OPTS = ['no_issue', 'needs_repair'];
const DYE_OPTS = ['passed', 'failed'];
const BOLT_OPTS = ['no_issue', 'needs_repair'];
const PEOPLE_OPTS = ['1', '2', '3', '4', '5', '6+'];
const YN_OPTS = ['yes', 'no'];
const YNNA_OPTS = ['yes', 'no'];
const HRV_OPTS = ['reduce', 'normal', 'off'];
const FURNACE_OPTS = ['clean', 'replaced'];
const STATUS_OPTS = ['passed', 'has issues'];

const FLOORING_STR_OPTS = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
const PLUMBING_LABEL_OPTS = [{ value: 'no_issue', label: 'No Issue' }, { value: 'needs_repair', label: 'Needs Repair' }];
const BOLT_LABEL_OPTS = [{ value: 'no_issue', label: 'No Issue' }, { value: 'needs_repair', label: 'Needs Repair' }];
const CLEANLINESS_LABEL_OPTS = [{ value: 'clean', label: 'Clean' }, { value: 'okay', label: 'Okay' }, { value: 'dirty', label: 'Dirty' }];

const inlineSelectStyle = (color = '#6366F1') => ({
  container: b => ({ ...b, minWidth: 110 }),
  control: (b, { isFocused }) => ({
    ...b, minHeight: 30, fontSize: '11px', borderRadius: 7,
    borderColor: isFocused ? color : '#CBD5E1', borderWidth: '1.5px',
    boxShadow: isFocused ? `0 0 0 2px ${color}22` : 'none',
    background: '#FAFBFF', cursor: 'pointer',
  }),
  valueContainer: b => ({ ...b, padding: '0 8px' }),
  dropdownIndicator: b => ({ ...b, padding: '0 4px', color: '#94A3B8' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: b => ({ ...b, fontSize: '11px', zIndex: 99999, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,28,46,0.15)', border: '1px solid #E2E8F0' }),
  option: (b, { isSelected, isFocused }) => ({ ...b, backgroundColor: isSelected ? color : isFocused ? `${color}14` : 'white', color: isSelected ? 'white' : '#0F172A', fontWeight: isSelected ? 700 : 400, padding: '6px 10px', borderRadius: 5, margin: '1px 2px', width: 'calc(100% - 4px)' }),
  singleValue: b => ({ ...b, color: '#0F172A', fontWeight: 600, fontSize: '11px' }),
  placeholder: b => ({ ...b, color: '#CBD5E1', fontSize: '11px' }),
  menuPortal: b => ({ ...b, zIndex: 99999 }),
});

const makeOpts = arr => arr.map(v => ({ value: v, label: cap(v) }));

const InlineNumberStepper = ({ value, onChange, emoji, min = 0, max = 20 }) => {
  const n = parseInt(value, 10) || 0;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button type="button" onClick={() => onChange(Math.max(min, n - 1))} style={{ width: 20, height: 20, border: '1px solid #E2E8F0', borderRadius: 5, background: '#F8FAFC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#6366F1', fontWeight: 700 }}>−</button>
      <span style={{ fontSize: '13px' }}>{emoji}</span>
      <span style={{ minWidth: 18, textAlign: 'center', fontWeight: 800, fontSize: '13px', color: n > 0 ? '#6366F1' : '#94A3B8' }}>{n}</span>
      <button type="button" onClick={() => onChange(Math.min(max, n + 1))} style={{ width: 20, height: 20, border: '1px solid #E2E8F0', borderRadius: 5, background: '#F8FAFC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#6366F1', fontWeight: 700 }}>+</button>
    </div>
  );
};

const InlineRow = ({ row, cols, colWidths, isApt, unitOptions, onSave, onDelete, onOpenHistory, onOpenHistoric, canEdit, buildingType }) => {
  const isDraftRow = row.is_draft === true;
  const hasMultipleInspections = (row._allInspections?.length || 0) > 1;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingKeys, setExistingKeys] = useState([]);
  const rowRef = useRef(null);
  const startEdit = useCallback(() => {
    if (row.isPending) return;
    setForm({
      unit_number: row.unit_number || '',
      inspection_date: row.inspection_date ? new Date(row.inspection_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      cleanliness: row.cleanliness || '',
      any_damages: row.any_damages ?? false,
      number_of_dogs: row.number_of_dogs ?? 0,
      number_of_cats: row.number_of_cats ?? 0,
      plumbing: row.plumbing || '',
      dye_test: row.dye_test || '',
      lubricate_dead_bolt: row.lubricate_dead_bolt || '',
      number_of_people: row.number_of_people ? String(row.number_of_people) : '',
      signs_of_smoking: row.signs_of_smoking ?? false,
      closed_window: row.closed_window || '',
      furniture_away: row.furniture_away || '',
      hrv_setting: row.hrv_setting || '',
      furnace_filter: row.furnace_filter || '',
      flooring_damage: row.flooring_damage || '',
      overall_status: row.overall_status || '',
      comments: row.comments || '',
      is_draft: row.is_draft ?? false,
    });
    setPendingFiles([]);
    setExistingKeys(row.attachments || []);
    setEditing(true);
  }, [row]);
  const cancelEdit = async () => {
    const hasChanges = pendingFiles.length > 0 ||
      Object.keys(form).some(k => {
        const orig = {
          unit_number: row.unit_number || '',
          inspection_date: row.inspection_date ? new Date(row.inspection_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          cleanliness: row.cleanliness || '', any_damages: row.any_damages ?? false,
          number_of_dogs: row.number_of_dogs ?? 0, number_of_cats: row.number_of_cats ?? 0,
          plumbing: row.plumbing || '', dye_test: row.dye_test || '',
          lubricate_dead_bolt: row.lubricate_dead_bolt || '',
          number_of_people: row.number_of_people ? String(row.number_of_people) : '',
          signs_of_smoking: row.signs_of_smoking ?? false,
          closed_window: row.closed_window || '', furniture_away: row.furniture_away || '',
          hrv_setting: row.hrv_setting || '', furnace_filter: row.furnace_filter || '',
          flooring_damage: row.flooring_damage || '',
          overall_status: row.overall_status || '', comments: row.comments || '',
          is_draft: row.is_draft ?? false,
        };
        return form[k] !== orig[k];
      });
    if (!hasChanges) { setPendingFiles([]); setEditing(false); return; }
    const result = await Swal.fire({
      title: 'Discard changes?', text: 'Unsaved edits will be lost.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#EF4444', cancelButtonColor: '#94A3B8',
      confirmButtonText: 'Yes, discard', cancelButtonText: 'Keep editing', width: 380,
    });
    if (result.isConfirmed) { setPendingFiles([]); setEditing(false); }
  };
  const commitSave = async (isDraft = false) => {
    setSaving(true);
    try {
      const payload = { ...row, ...form, attachments: existingKeys, is_draft: isDraft, _pendingFiles: pendingFiles };
      await onSave(payload);
      setPendingFiles([]);
      setEditing(false);
    } catch (err) {
      console.error('Inline save failed:', err);
    } finally {
      setSaving(false);
    }
  };
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const cw = key => colWidths[key] || FACTORY_WIDTHS[key] || 110;
  const btnRef = useRef(null);
  if (editing) {
    return (
      <tr ref={rowRef} className="si-row si-row--editing" style={{ background: 'linear-gradient(135deg, #F0F4FF, #F8F0FF)', outline: '2px solid #6366F1', outlineOffset: -1 }}>
        {cols.map(col => {
          const w = cw(col.key);
          if (col.key === 'expand') return <td key="exp" className="si-expand-td" style={{ width: w }}><div className="si-expand-icon" style={{ background: '#6366F115' }}><FeatherIcon icon="edit-2" size={12} color="#6366F1" /></div></td>;
          if (col.key === 'actions') return (
            <td key="act" className="si-actions-td" style={{ width: w }} onClick={e => e.stopPropagation()}>
              <div className="si-action-group" style={{ flexDirection: 'column', gap: 4 }}>
                <button type="button" onClick={() => commitSave(false)} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', width: '100%', justifyContent: 'center' }}>
                  {saving ? <Spinner size="sm" style={{ width: 10, height: 10 }} /> : <FeatherIcon icon="check" size={11} />} Save
                </button>
                <button type="button" onClick={() => commitSave(true)} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1.5px solid #FCD34D', background: '#FFFBEB', color: '#92400E', fontSize: '11px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', width: '100%', justifyContent: 'center' }}>
                  <FeatherIcon icon="edit-3" size={11} /> Draft
                </button>
                <button type="button" onClick={cancelEdit} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1.5px solid #E2E8F0', background: 'white', color: '#64748B', fontSize: '11px', fontWeight: 700, cursor: 'pointer', width: '100%', justifyContent: 'center' }}>
                  <FeatherIcon icon="x" size={11} /> Cancel
                </button>
              </div>
            </td>
          );
          if (col.key === 'attachments') return (
            <td key="att" style={{ width: w }} onClick={e => e.stopPropagation()}>
              <InlineAttachmentCell
                existingKeys={existingKeys}
                pendingFiles={pendingFiles}
                onPendingChange={setPendingFiles}
                onExistingChange={setExistingKeys}
              />
            </td>
          );
          if (col.key === 'unit_number') return <td key="unit" className="si-sticky" style={{ width: w }}><span className="si-cell-unit">{row.unit_number}</span></td>;
          if (col.key === 'inspection_date') return (
            <td key="date" style={{ width: w }}>
              <input type="date" value={form.inspection_date || ''} max={new Date().toISOString().split('T')[0]} onChange={e => setF('inspection_date', e.target.value)} style={{ width: '100%', height: 30, border: '1.5px solid #E2E8F0', borderRadius: 7, padding: '0 6px', fontSize: '11px', outline: 'none', background: '#FAFBFF' }} />
            </td>
          );
          if (col.key === 'cleanliness') return <td key="clean" style={{ width: w }}><Select options={CLEANLINESS_LABEL_OPTS} value={form.cleanliness ? CLEANLINESS_LABEL_OPTS.find(o => o.value === form.cleanliness) || null : null} onChange={o => setF('cleanliness', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'any_damages') return <td key="dmg" style={{ width: w }}><Select options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} value={form.any_damages !== undefined ? { value: form.any_damages, label: form.any_damages ? 'Yes' : 'No' } : null} onChange={o => setF('any_damages', o?.value)} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} placeholder="Select…" /></td>;
          if (col.key === 'number_of_dogs') return <td key="dogs" style={{ width: w }}><InlineNumberStepper value={form.number_of_dogs} onChange={v => setF('number_of_dogs', v)} emoji="🐶" /></td>;
          if (col.key === 'number_of_cats') return <td key="cats" style={{ width: w }}><InlineNumberStepper value={form.number_of_cats} onChange={v => setF('number_of_cats', v)} emoji="🐱" /></td>;
          if (col.key === 'plumbing') return <td key="plumb" style={{ width: w }}><Select options={PLUMBING_LABEL_OPTS} value={form.plumbing ? PLUMBING_LABEL_OPTS.find(o => o.value === form.plumbing) || null : null} onChange={o => setF('plumbing', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'dye_test') return <td key="dye" style={{ width: w }}><Select options={[{ value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' }]} value={form.dye_test ? { value: form.dye_test, label: form.dye_test === 'passed' ? 'Passed' : 'Failed' } : null} onChange={o => setF('dye_test', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'closed_window') return <td key="cw" style={{ width: w }}><Select options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.closed_window ? { value: form.closed_window, label: form.closed_window === 'yes' ? 'Yes' : 'No' } : null} onChange={o => setF('closed_window', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'furniture_away') return <td key="fa" style={{ width: w }}><Select options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.furniture_away ? { value: form.furniture_away, label: form.furniture_away === 'yes' ? 'Yes' : 'No' } : null} onChange={o => setF('furniture_away', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'hrv_setting') return <td key="hrv" style={{ width: w }}><Select options={[{ value: 'reduce', label: 'Reduce' }, { value: 'normal', label: 'Normal' }, { value: 'off', label: 'Off' }]} value={form.hrv_setting ? { value: form.hrv_setting, label: form.hrv_setting === 'reduce' ? 'Reduce' : form.hrv_setting === 'normal' ? 'Normal' : 'Off' } : null} onChange={o => setF('hrv_setting', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'furnace_filter') return <td key="ff" style={{ width: w }}><Select options={[{ value: 'clean', label: 'Clean' }, { value: 'replaced', label: 'Replaced' }]} value={form.furnace_filter ? { value: form.furnace_filter, label: form.furnace_filter === 'clean' ? 'Clean' : 'Replaced' } : null} onChange={o => setF('furnace_filter', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'flooring_damage') return <td key="fd" style={{ width: w }}><Select options={FLOORING_STR_OPTS} value={form.flooring_damage ? FLOORING_STR_OPTS.find(o => o.value === form.flooring_damage) || null : null} onChange={o => setF('flooring_damage', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} placeholder="Select…" isClearable /></td>;
          if (col.key === 'lubricate_dead_bolt') return <td key="bolt" style={{ width: w }}><Select options={BOLT_LABEL_OPTS} value={form.lubricate_dead_bolt ? BOLT_LABEL_OPTS.find(o => o.value === form.lubricate_dead_bolt) || null : null} onChange={o => setF('lubricate_dead_bolt', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'number_of_people') return <td key="ppl" style={{ width: w }}><Select options={PEOPLE_OPTS.map(v => ({ value: v, label: v }))} value={form.number_of_people ? { value: form.number_of_people, label: form.number_of_people } : null} onChange={o => setF('number_of_people', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
          if (col.key === 'signs_of_smoking') return <td key="smoke" style={{ width: w }}><Select options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} value={form.signs_of_smoking !== undefined ? { value: form.signs_of_smoking, label: form.signs_of_smoking ? 'Yes' : 'No' } : null} onChange={o => setF('signs_of_smoking', o?.value)} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} placeholder="Select…" /></td>;
          if (col.key === 'overall_status') return <td key="status" style={{ width: w }}><Select options={[{ value: 'passed', label: 'Passed' }, { value: 'has issues', label: 'Has Issues' }]} value={form.overall_status ? { value: form.overall_status, label: form.overall_status === 'passed' ? 'Passed' : 'Has Issues' } : null} onChange={o => setF('overall_status', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle(form.overall_status === 'has issues' ? '#EF4444' : '#10B981')} placeholder="Select…" isClearable /></td>;
          if (col.key === 'comments') return <td key="comments" style={{ width: w }}><input type="text" value={form.comments || ''} onChange={e => setF('comments', e.target.value)} placeholder="Add notes…" style={{ width: '100%', height: 30, border: '1.5px solid #E2E8F0', borderRadius: 7, padding: '0 8px', fontSize: '11px', outline: 'none', background: '#FAFBFF' }} /></td>;
          return <td key={col.key} style={{ width: w }}><span className="si-cell-muted">—</span></td>;
        })}
      </tr>
    );
  }
  return (
    <tr
      className={`si-row${row.isPending ? ' si-pending' : isDraftRow ? ' si-draft' : row.overall_status === 'has issues' ? ' si-issues' : ''}`}
      onClick={() => canEdit && !row.isPending && startEdit()}
      style={{ cursor: canEdit && !row.isPending ? 'pointer' : 'default' }}
    >
      {cols.map(col => {
        const w = cw(col.key);
        if (col.key === 'expand') {
          return row.isPending
            ? <td key="exp" className="si-expand-td" style={{ width: w }}><div className="si-expand-icon si-expand-icon--pending"><FeatherIcon icon="alert-circle" size={12} color="#DC2626" /></div></td>
            : <td key="exp" className="si-expand-td" style={{ width: w }}>{canEdit && <div className="si-expand-icon si-expand-icon--view"><FeatherIcon icon={isDraftRow ? 'edit-3' : 'edit-3'} size={12} color={isDraftRow ? '#D97706' : '#6366F1'} /></div>}</td>;
        }
        if (col.key === 'actions') {
          return (
            <td key="act" className="si-actions-td" style={{ width: w }} onClick={e => e.stopPropagation()}>
              <div className="si-action-group">
                {!row.isPending && <HistoryTriggerButton row={row} onOpen={onOpenHistory} />}
                {!row.isPending && hasMultipleInspections && (
                  <Tooltip title={`View all ${row._allInspections.length} inspections`} arrow>
                    <button type="button" className="si-ebtn" onClick={e => { e.stopPropagation(); onOpenHistoric(row); }} style={{ background: '#EEF2FF', borderColor: '#6366F1', color: '#4F46E5', position: 'relative' }}>
                      <FeatherIcon icon="archive" size={13} color="#4F46E5" />
                      <span style={{ position: 'absolute', top: -5, right: -5, width: 14, height: 14, background: '#6366F1', color: 'white', borderRadius: '50%', fontSize: '8px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row._allInspections.length}</span>
                    </button>
                  </Tooltip>
                )}
                {canEdit && !row.isPending && (
                  <Tooltip title={isDraftRow ? 'Edit draft' : 'Edit inline'} arrow>
                    <button type="button" className="si-ebtn" style={isDraftRow ? { borderColor: '#FCD34D', background: '#FFFBEB' } : {}} onClick={e => { e.stopPropagation(); startEdit(); }}>
                      <FeatherIcon icon={isDraftRow ? 'edit-3' : 'edit-2'} size={13} color={isDraftRow ? '#D97706' : '#6366F1'} />
                    </button>
                  </Tooltip>
                )}
                {canEdit && !row.isPending && (
                  <Tooltip title="Delete" arrow>
                    <button type="button" className="si-dbtn" onClick={e => { e.stopPropagation(); onDelete(row); }}>
                      <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                    </button>
                  </Tooltip>
                )}
                {canEdit && row.isPending && (
                  <Tooltip title="Start inspection" arrow>
                    <button type="button" className="si-btn si-btn--inspect" onClick={e => { e.stopPropagation(); startEdit(); }}>
                      <FeatherIcon icon="plus" size={11} /> Inspect
                    </button>
                  </Tooltip>
                )}
              </div>
            </td>
          );
        }
        if (col.key === 'attachments') {
          return row.isPending
            ? <td key="att" className="si-cell-center" style={{ width: w }}><span className="si-cell-muted">—</span></td>
            : <td key="att" className={col.sticky ? 'si-sticky' : ''} style={{ width: w }} onClick={e => e.stopPropagation()}><AttachmentViewCell row={row} /></td>;
        }
        if (row.isPending) {
          if (col.key === 'unit_number') return <td key="unit" className="si-sticky si-pending-unit-td" style={{ width: w }}><span className="si-cell-unit si-cell-unit--pending">{row.unit_number}</span></td>;
          if (col.key === 'inspection_date') return <td key="date" className="si-pending-date-td" style={{ width: w }}><span className="si-badge si-badge--not-inspected"><FeatherIcon icon="clock" size={9} /> Not Inspected</span></td>;
          if (col.key === 'overall_status') return <td key="status" className="si-cell-center" style={{ width: w }}><span className="si-badge si-badge--pending-status"><FeatherIcon icon="minus-circle" size={9} /> Pending</span></td>;
          return <td key={col.key} className="si-cell-center" style={{ width: w }}><span className="si-cell-muted si-cell-muted--faint">—</span></td>;
        }
        return (
          <td key={col.key} className={col.sticky ? 'si-sticky' : ''} style={{ width: w }}>
            <span className="si-cell-inner">{cellVal(row, col.key)}</span>
          </td>
        );
      })}
    </tr>
  );
};

const InlinePendingCreateRow = ({ unitNumber, cols, colWidths, isApt, unitOptions, buildingType, onSave, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [form, setForm] = useState({
    unit_number: unitNumber,
    inspection_date: new Date().toISOString().split('T')[0],
    cleanliness: '', any_damages: false, number_of_dogs: 0, number_of_cats: 0,
    plumbing: '', dye_test: '', lubricate_dead_bolt: '', number_of_people: '',
    signs_of_smoking: false, closed_window: '', furniture_away: '', hrv_setting: '',
    furnace_filter: '', flooring_damage: '', overall_status: '', comments: '',
    attachments: [], is_draft: false,
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const cw = key => colWidths[key] || FACTORY_WIDTHS[key] || 110;
  const commitSave = async (isDraft = false) => {
    setSaving(true);
    try {
      const payload = { ...form, unit_number: unitNumber, attachments: [], is_draft: isDraft, _pendingFiles: pendingFiles };
      await onSave(payload);
      setPendingFiles([]);
      setExpanded(false);
    } catch (err) {
      console.error('Inline create failed:', err);
    } finally {
      setSaving(false);
    }
  };
  if (!expanded) {
    return (
      <tr className="si-row si-pending" onClick={() => canEdit && setExpanded(true)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
        {cols.map(col => {
          const w = cw(col.key);
          if (col.key === 'expand') return <td key="exp" className="si-expand-td" style={{ width: w }}><div className="si-expand-icon si-expand-icon--pending"><FeatherIcon icon="alert-circle" size={12} color="#DC2626" /></div></td>;
          if (col.key === 'unit_number') return <td key="unit" className="si-sticky si-pending-unit-td" style={{ width: w }}><span className="si-cell-unit si-cell-unit--pending">{unitNumber}</span></td>;
          if (col.key === 'inspection_date') return <td key="date" className="si-pending-date-td" style={{ width: w }}><span className="si-badge si-badge--not-inspected"><FeatherIcon icon="clock" size={9} /> Not Inspected</span></td>;
          if (col.key === 'overall_status') return <td key="status" className="si-cell-center" style={{ width: w }}><span className="si-badge si-badge--pending-status"><FeatherIcon icon="minus-circle" size={9} /> Pending</span></td>;
          if (col.key === 'actions') return (
            <td key="act" className="si-actions-td" style={{ width: w }} onClick={e => e.stopPropagation()}>
              <div className="si-action-group">
                {canEdit && <Tooltip title="Start inspection" arrow><button type="button" className="si-btn si-btn--inspect" onClick={e => { e.stopPropagation(); setExpanded(true); }}><FeatherIcon icon="plus" size={11} /> Inspect</button></Tooltip>}
              </div>
            </td>
          );
          return <td key={col.key} className="si-cell-center" style={{ width: w }}><span className="si-cell-muted si-cell-muted--faint">—</span></td>;
        })}
      </tr>
    );
  }
  return (
    <tr className="si-row si-row--editing" style={{ background: 'linear-gradient(135deg, #F0FFF4, #F0F4FF)', outline: '2px solid #10B981', outlineOffset: -1 }}>
      {cols.map(col => {
        const w = cw(col.key);
        if (col.key === 'expand') return <td key="exp" className="si-expand-td" style={{ width: w }}><div className="si-expand-icon" style={{ background: '#10B98115' }}><FeatherIcon icon="plus" size={12} color="#10B981" /></div></td>;
        if (col.key === 'unit_number') return <td key="unit" className="si-sticky" style={{ width: w }}><span className="si-cell-unit" style={{ color: '#10B981', fontWeight: 800 }}>{unitNumber}</span></td>;
        if (col.key === 'actions') return (
          <td key="act" className="si-actions-td" style={{ width: w }} onClick={e => e.stopPropagation()}>
            <div className="si-action-group" style={{ flexDirection: 'column', gap: 4 }}>
              <button type="button" onClick={() => commitSave(false)} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', width: '100%', justifyContent: 'center' }}>
                {saving ? <Spinner size="sm" style={{ width: 10, height: 10 }} /> : <FeatherIcon icon="check" size={11} />} Save
              </button>
              <button type="button" onClick={() => commitSave(true)} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1.5px solid #FCD34D', background: '#FFFBEB', color: '#92400E', fontSize: '11px', fontWeight: 700, cursor: 'pointer', width: '100%', justifyContent: 'center' }}>
                <FeatherIcon icon="edit-3" size={11} /> Draft
              </button>
              <button type="button" onClick={async () => {
                const hasInput = pendingFiles.length > 0 || Object.values(form).some((v, i) => {
                  const keys = Object.keys(form);
                  const defaults = { unit_number: unitNumber, inspection_date: new Date().toISOString().split('T')[0], cleanliness: '', any_damages: false, number_of_dogs: 0, number_of_cats: 0, plumbing: '', dye_test: '', lubricate_dead_bolt: '', number_of_people: '', signs_of_smoking: false, closed_window: '', furniture_away: '', hrv_setting: '', furnace_filter: '', flooring_damage: '', overall_status: '', comments: '', attachments: [], is_draft: false };
                  return v !== defaults[keys[i]];
                });
                if (!hasInput) { setExpanded(false); return; }
                const r = await Swal.fire({ title: 'Discard entry?', text: 'Unsaved data will be lost.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#EF4444', cancelButtonColor: '#94A3B8', confirmButtonText: 'Yes, discard', cancelButtonText: 'Keep editing', width: 380 });
                if (r.isConfirmed) setExpanded(false);
              }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1.5px solid #E2E8F0', background: 'white', color: '#64748B', fontSize: '11px', fontWeight: 700, cursor: 'pointer', width: '100%', justifyContent: 'center' }}>
                <FeatherIcon icon="x" size={11} /> Cancel
              </button>
            </div>
          </td>
        );
        if (col.key === 'attachments') return (
          <td key="att" style={{ width: w }} onClick={e => e.stopPropagation()}>
            <InlineAttachmentCell
              existingKeys={[]}
              pendingFiles={pendingFiles}
              onPendingChange={setPendingFiles}
              onExistingChange={() => { }}
            />
          </td>
        );
        if (col.key === 'inspection_date') return <td key="date" style={{ width: w }}><input type="date" value={form.inspection_date || ''} max={new Date().toISOString().split('T')[0]} onChange={e => setF('inspection_date', e.target.value)} style={{ width: '100%', height: 30, border: '1.5px solid #E2E8F0', borderRadius: 7, padding: '0 6px', fontSize: '11px', outline: 'none', background: '#FAFBFF' }} /></td>;
        if (col.key === 'cleanliness') return <td key="clean" style={{ width: w }}><Select options={CLEANLINESS_LABEL_OPTS} value={form.cleanliness ? CLEANLINESS_LABEL_OPTS.find(o => o.value === form.cleanliness) || null : null} onChange={o => setF('cleanliness', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'any_damages') return <td key="dmg" style={{ width: w }}><Select options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} value={{ value: form.any_damages, label: form.any_damages ? 'Yes' : 'No' }} onChange={o => setF('any_damages', o?.value)} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} /></td>;
        if (col.key === 'number_of_dogs') return <td key="dogs" style={{ width: w }}><InlineNumberStepper value={form.number_of_dogs} onChange={v => setF('number_of_dogs', v)} emoji="🐶" /></td>;
        if (col.key === 'number_of_cats') return <td key="cats" style={{ width: w }}><InlineNumberStepper value={form.number_of_cats} onChange={v => setF('number_of_cats', v)} emoji="🐱" /></td>;
        if (col.key === 'plumbing') return <td key="plumb" style={{ width: w }}><Select options={PLUMBING_LABEL_OPTS} value={form.plumbing ? PLUMBING_LABEL_OPTS.find(o => o.value === form.plumbing) || null : null} onChange={o => setF('plumbing', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'dye_test') return <td key="dye" style={{ width: w }}><Select options={[{ value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' }]} value={form.dye_test ? { value: form.dye_test, label: form.dye_test === 'passed' ? 'Passed' : 'Failed' } : null} onChange={o => setF('dye_test', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'closed_window') return <td key="cw" style={{ width: w }}><Select options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.closed_window ? { value: form.closed_window, label: form.closed_window === 'yes' ? 'Yes' : 'No' } : null} onChange={o => setF('closed_window', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'furniture_away') return <td key="fa" style={{ width: w }}><Select options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={form.furniture_away ? { value: form.furniture_away, label: form.furniture_away === 'yes' ? 'Yes' : 'No' } : null} onChange={o => setF('furniture_away', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'hrv_setting') return <td key="hrv" style={{ width: w }}><Select options={[{ value: 'reduce', label: 'Reduce' }, { value: 'normal', label: 'Normal' }, { value: 'off', label: 'Off' }]} value={form.hrv_setting ? { value: form.hrv_setting, label: form.hrv_setting === 'reduce' ? 'Reduce' : form.hrv_setting === 'normal' ? 'Normal' : 'Off' } : null} onChange={o => setF('hrv_setting', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'furnace_filter') return <td key="ff" style={{ width: w }}><Select options={[{ value: 'clean', label: 'Clean' }, { value: 'replaced', label: 'Replaced' }]} value={form.furnace_filter ? { value: form.furnace_filter, label: form.furnace_filter === 'clean' ? 'Clean' : 'Replaced' } : null} onChange={o => setF('furnace_filter', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'flooring_damage') return <td key="fd" style={{ width: w }}><Select options={FLOORING_STR_OPTS} value={form.flooring_damage ? FLOORING_STR_OPTS.find(o => o.value === form.flooring_damage) || null : null} onChange={o => setF('flooring_damage', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} placeholder="Select…" isClearable /></td>;
        if (col.key === 'lubricate_dead_bolt') return <td key="bolt" style={{ width: w }}><Select options={BOLT_LABEL_OPTS} value={form.lubricate_dead_bolt ? BOLT_LABEL_OPTS.find(o => o.value === form.lubricate_dead_bolt) || null : null} onChange={o => setF('lubricate_dead_bolt', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'number_of_people') return <td key="ppl" style={{ width: w }}><Select options={PEOPLE_OPTS.map(v => ({ value: v, label: v }))} value={form.number_of_people ? { value: form.number_of_people, label: form.number_of_people } : null} onChange={o => setF('number_of_people', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'signs_of_smoking') return <td key="smoke" style={{ width: w }}><Select options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} value={{ value: form.signs_of_smoking, label: form.signs_of_smoking ? 'Yes' : 'No' }} onChange={o => setF('signs_of_smoking', o?.value)} menuPortalTarget={document.body} styles={inlineSelectStyle('#EF4444')} /></td>;
        if (col.key === 'overall_status') return <td key="status" style={{ width: w }}><Select options={[{ value: 'passed', label: 'Passed' }, { value: 'has issues', label: 'Has Issues' }]} value={form.overall_status ? { value: form.overall_status, label: form.overall_status === 'passed' ? 'Passed' : 'Has Issues' } : null} onChange={o => setF('overall_status', o?.value || '')} menuPortalTarget={document.body} styles={inlineSelectStyle()} placeholder="Select…" isClearable /></td>;
        if (col.key === 'comments') return <td key="comments" style={{ width: w }}><input type="text" value={form.comments || ''} onChange={e => setF('comments', e.target.value)} placeholder="Notes…" style={{ width: '100%', height: 30, border: '1.5px solid #E2E8F0', borderRadius: 7, padding: '0 8px', fontSize: '11px', outline: 'none', background: '#FAFBFF' }} /></td>;
        return <td key={col.key} style={{ width: w }}><span className="si-cell-muted">—</span></td>;
      })}
    </tr>
  );
};

const ViewModeToggle = ({ mode, onChange }) => {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      background: '#F1F5F9', borderRadius: 10, padding: 3,
      border: '1px solid #E2E8F0',
    }}>
      {[
        { key: 'inline', icon: 'grid', label: 'Inline', tooltip: 'Excel-style inline editing' },
        { key: 'modal', icon: 'layers', label: 'Modal', tooltip: 'Guided modal form' },
      ].map(opt => (
        <Tooltip key={opt.key} title={opt.tooltip} arrow placement="bottom">
          <button
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 8, border: 'none',
              background: mode === opt.key ? 'white' : 'transparent',
              color: mode === opt.key ? '#6366F1' : '#94A3B8',
              fontWeight: mode === opt.key ? 700 : 500,
              fontSize: '12px', cursor: 'pointer',
              boxShadow: mode === opt.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <FeatherIcon icon={opt.icon} size={13} color={mode === opt.key ? '#6366F1' : '#94A3B8'} />
            {opt.label}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

const SuiteInspectionListTable = ({
  items = [],
  masterSheetUnits = [],
  onDelete,
  onSaveRow,
  onRefresh,
  userRole,
  buildingType = 'Apartment',
  filters = {},
  scrollThreshold = 992,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [colWidths, setColWidths] = useState(loadSavedWidths);
  const [showSettings, setShowSettings] = useState(false);
  const [historyPopover, setHistoryPopover] = useState({ anchorEl: null, inspectionId: null, unitNumber: '' });
  const [viewMode, setViewMode] = useState(loadViewMode);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalInitialData, setModalInitialData] = useState(null);
  const [historicModalOpen, setHistoricModalOpen] = useState(false);
  const [historicUnit, setHistoricUnit] = useState({ unitNumber: '', inspections: [] });
  const isApt = buildingType !== 'Townhouse';
  const canEdit = userRole === 'company' || userRole === 'manager'  || userRole === 'technical staff';
  useEffect(() => { const fn = () => setWindowWidth(window.innerWidth); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  useEffect(() => {
    const handler = e => { if (showSettings && !e.target.closest('.si-settings-panel') && !e.target.closest('.si-settings-btn')) setShowSettings(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };
  const openHistory = useCallback((anchorEl, inspectionId, unitNumber) => setHistoryPopover({ anchorEl, inspectionId, unitNumber }), []);
  const closeHistory = useCallback(() => setHistoryPopover({ anchorEl: null, inspectionId: null, unitNumber: '' }), []);
  const unitMap = useMemo(() => {
    const map = {};
    items.forEach(item => {
      const u = String(item.unit_number || '').trim().toLowerCase();
      if (!u) return;
      if (!map[u]) map[u] = [];
      map[u].push(item);
    });
    for (const u in map) {
      map[u].sort((a, b) => {
        const getCreatedDate = (obj) => obj.created_at || obj.createdAt || obj.inspection_date;
        const dateA = new Date(getCreatedDate(b) || 0);
        const dateB = new Date(getCreatedDate(a) || 0);
        return dateA - dateB;
      });
    }
    return map;
  }, [items]);
  const displayRows = useMemo(() => {
    if (!masterSheetUnits.length || !filters.property_id) return [];
    return masterSheetUnits
      .map(unit => {
        const unitNo = String(unit.unitNo || unit.unit_number || '').trim();
        const lower = unitNo.toLowerCase();
        const allInspections = unitMap[lower] || [];
        const latest = allInspections[0];
        if (latest) {
          return { ...latest, overall_status: latest?.overall_status ?? latest?.overallStatus ?? null, isPending: false, _allInspections: allInspections };
        }
        return {
          unit_number: unitNo, isPending: true, inspection_date: null,
          cleanliness: null, any_damages: null, number_of_dogs: null, number_of_cats: null,
          plumbing: null, dye_test: null, closed_window: null, furniture_away: null,
          hrv_setting: null, furnace_filter: null, flooring_damage: null,
          lubricate_dead_bolt: null, number_of_people: null, signs_of_smoking: null,
          overall_status: null, attachments: [], comments: null, _id: null, _allInspections: [],
        };
      })
      .sort((a, b) => {
        const ua = String(a.unit_number || '').trim().toLowerCase();
        const ub = String(b.unit_number || '').trim().toLowerCase();
        return ua.localeCompare(ub, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [masterSheetUnits, unitMap, filters.property_id]);
  const totals = useMemo(() => {
    if (!displayRows.length) return null;
    const inspected = displayRows.filter(r => !r.isPending);
    return {
      total: inspected.length,
      issues: inspected.filter(r => r.overall_status === 'has issues' && !r.is_draft).length,
      drafts: inspected.filter(r => r.is_draft).length,
    };
  }, [displayRows]);
  const adjustWidth = (key, delta) => setColWidths(prev => { const next = { ...prev, [key]: Math.max(60, (prev[key] || FACTORY_WIDTHS[key] || 110) + delta) }; saveWidths(next); return next; });
  const applySettings = newWidths => { setColWidths(newWidths); saveWidths(newWidths); toast.success('Column widths saved'); };
  const cw = key => colWidths[key] || FACTORY_WIDTHS[key] || 110;
  const unitOptions = useMemo(() => {
    const fromMaster = masterSheetUnits.map(u => ({ value: u.unitNo || u.unit_number, label: `Unit ${u.unitNo || u.unit_number}` }));
    return fromMaster;
  }, [masterSheetUnits]);
  const openCreate = (prefillUnit = null) => {
    setModalMode('create');
    setModalInitialData(prefillUnit ? { unit_number: String(prefillUnit.unitNo || prefillUnit.unit_number || '') } : null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    if (!canEdit || row.isPending) return;
    setModalMode('edit');
    setModalInitialData(transformInspectionForForm(row));
    setModalOpen(true);
  };

  const openHistoric = (row) => {
    if (row.isPending || !row._allInspections?.length) return;
    setHistoricUnit({ unitNumber: row.unit_number, inspections: row._allInspections });
    setHistoricModalOpen(true);
  };
  const handleInlineSave = useCallback(async (formData) => {
    const { _pendingFiles = [], ...payload } = formData;
    const isCreating = !payload._id;
    const isDraft = payload.is_draft === true;
    Swal.fire({
      html: `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:12px 0;font-family:system-ui,sans-serif"><div style="width:44px;height:44px;border:3px solid #EEF2FF;border-top-color:#6366F1;border-radius:50%;animation:si-spin 0.75s linear infinite"></div><div style="font-size:14px;font-weight:700;color:#0F172A">${isDraft ? 'Saving draft…' : isCreating ? 'Creating inspection…' : 'Saving changes…'}</div><style>@keyframes si-spin{to{transform:rotate(360deg)}}</style></div>`,
      showConfirmButton: false, allowOutsideClick: false, width: 300, padding: '24px',
    });
    try {
      const result = await onSaveRow(payload);
      if (_pendingFiles.length > 0) {
        const inspId = isCreating ? (result?.data?._id || result?._id) : payload._id;
        if (inspId) {
          Swal.fire({
            html: `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:12px 0;font-family:system-ui,sans-serif"><div style="width:44px;height:44px;border:3px solid #EEF2FF;border-top-color:#6366F1;border-radius:50%;animation:si-spin 0.75s linear infinite"></div><div style="font-size:14px;font-weight:700;color:#0F172A">Uploading ${_pendingFiles.length} file(s)…</div><style>@keyframes si-spin{to{transform:rotate(360deg)}}</style></div>`,
            showConfirmButton: false, allowOutsideClick: false, width: 300, padding: '24px',
          });
          await doUploadFiles(inspId, _pendingFiles);
        } else {
          toast.warn('Inspection saved but files could not be uploaded — please edit the row to add them.');
        }
      }
      const doneIcon = isDraft ? '📝' : '✓';
      const doneTitle = isDraft ? (isCreating ? 'Draft Saved!' : 'Draft Updated!') : (isCreating ? 'Inspection Created!' : 'Changes Saved!');
      await Swal.fire({
        html: `<div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:10px 0;font-family:system-ui,sans-serif"><div style="width:56px;height:56px;background:${isDraft ? 'linear-gradient(135deg,#FEF3C7,#FDE68A)' : 'linear-gradient(135deg,#D1FAE5,#A7F3D0)'};border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:26px;box-shadow:0 4px 16px rgba(16,185,129,0.2)">${doneIcon}</div><div style="font-size:16px;font-weight:800;color:#0F172A">${doneTitle}</div><div style="font-size:12px;color:#64748B">Unit ${payload.unit_number || ''}</div></div>`,
        showConfirmButton: false, timer: 1400, width: 300, padding: '24px',
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      await Swal.fire({ title: 'Something went wrong', icon: 'error', confirmButtonColor: '#EF4444', width: 380 });
    }
  }, [onSaveRow, onRefresh]);
  const handleModalSave = useCallback(async (formData) => {
    const { _pendingFiles = [], ...payload } = formData;
    const isCreating = modalMode === 'create';
    const isDraft = payload.is_draft === true;
    Swal.fire({
      html: `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:12px 0;font-family:system-ui,sans-serif"><div style="width:44px;height:44px;border:3px solid #EEF2FF;border-top-color:#6366F1;border-radius:50%;animation:si-spin 0.75s linear infinite"></div><div style="font-size:14px;font-weight:700;color:#0F172A">${isDraft ? 'Saving draft…' : isCreating ? 'Creating inspection…' : 'Saving changes…'}</div><style>@keyframes si-spin{to{transform:rotate(360deg)}}</style></div>`,
      showConfirmButton: false, allowOutsideClick: false, width: 300, padding: '24px',
    });
    try {
      const result = await onSaveRow(payload);
      if (_pendingFiles.length > 0) {
        const inspId = isCreating ? (result?.data?._id || result?._id) : payload._id;
        if (inspId) {
          Swal.fire({
            html: `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:12px 0;font-family:system-ui,sans-serif"><div style="width:44px;height:44px;border:3px solid #EEF2FF;border-top-color:#6366F1;border-radius:50%;animation:si-spin 0.75s linear infinite"></div><div style="font-size:14px;font-weight:700;color:#0F172A">Uploading ${_pendingFiles.length} file(s)…</div><style>@keyframes si-spin{to{transform:rotate(360deg)}}</style></div>`,
            showConfirmButton: false, allowOutsideClick: false, width: 300, padding: '24px',
          });
          await doUploadFiles(inspId, _pendingFiles);
        } else {
          toast.warn('Inspection saved but files could not be uploaded — please edit the row to add them.');
        }
      }
      const doneIcon = isDraft ? '📝' : '✓';
      const doneTitle = isDraft ? (isCreating ? 'Draft Saved!' : 'Draft Updated!') : (isCreating ? 'Inspection Created!' : 'Changes Saved!');
      await Swal.fire({
        html: `<div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:10px 0;font-family:system-ui,sans-serif"><div style="width:56px;height:56px;background:${isDraft ? 'linear-gradient(135deg,#FEF3C7,#FDE68A)' : 'linear-gradient(135deg,#D1FAE5,#A7F3D0)'};border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:26px;box-shadow:0 4px 16px rgba(16,185,129,0.2)">${doneIcon}</div><div style="font-size:16px;font-weight:800;color:#0F172A">${doneTitle}</div><div style="font-size:12px;color:#64748B">Unit ${payload.unit_number || ''}</div></div>`,
        showConfirmButton: false, timer: 1400, width: 300, padding: '24px',
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      await Swal.fire({ title: 'Something went wrong', icon: 'error', confirmButtonColor: '#EF4444', width: 380 });
    }
  }, [modalMode, onSaveRow, onRefresh]);
  const cols = useMemo(() => {
    const base = [
      { key: 'expand', label: '' }, { key: 'inspection_date', label: 'Date' },
      { key: 'unit_number', label: 'Unit #', sticky: true }, { key: 'cleanliness', label: 'Cleanliness' },
      { key: 'any_damages', label: 'Damages' }, { key: 'number_of_dogs', label: '🐶 Dogs' }, { key: 'number_of_cats', label: '🐱 Cats' },
      { key: 'plumbing', label: 'Plumbing' }, { key: 'dye_test', label: 'Dye Test' },
    ];
    const apt = [{ key: 'closed_window', label: 'Closed Window' }, { key: 'furniture_away', label: 'Furniture Away' }];
    const th = [{ key: 'hrv_setting', label: 'HRV Setting' }, { key: 'furnace_filter', label: 'Furnace Filter' }, { key: 'flooring_damage', label: 'Flooring Dmg' }];
    const shared = [
      { key: 'lubricate_dead_bolt', label: 'Dead Bolt' }, { key: 'number_of_people', label: 'People' },
      { key: 'signs_of_smoking', label: 'Smoking' }, { key: 'overall_status', label: 'Status' },
      { key: 'attachments', label: 'Files' }, { key: 'comments', label: 'Comments' }, { key: 'actions', label: '' },
    ];
    return [...base, ...(isApt ? apt : th), ...shared];
  }, [isApt]);
  const maxH = windowWidth < scrollThreshold ? '62vh' : '68vh';
  return (
    <>
      <Paper elevation={0} className="si-wrap si-paper">
        <div className="si-header">
          <div className="si-header__left">
            <div className="si-header__icon-wrap"><FeatherIcon icon={isApt ? 'grid' : 'home'} size={18} color="#93C5FD" /></div>
            <div className="si-header__meta">
              <div className="si-header__label">Suite Inspections</div>
              <div className="si-header__title">{filters.property_id ? (isApt ? 'Apartments' : 'Townhouses') : 'Select a Property to Begin'}</div>
            </div>
            <div className="si-header__badges">
              {displayRows.length > 0 && <span className="si-header__badge si-header__badge--records">{displayRows.length} unit{displayRows.length !== 1 ? 's' : ''}</span>}
              {totals?.issues > 0 && <span className="si-header__badge si-header__badge--issues">{totals.issues} with issues</span>}
              {totals?.drafts > 0 && <span className="si-header__badge" style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}>{totals.drafts} draft{totals.drafts !== 1 ? 's' : ''}</span>}
            </div>
          </div>
          <div className="si-header__actions">
            <ViewModeToggle mode={viewMode} onChange={handleViewModeChange} />
            <Tooltip title="Column width settings" arrow>
              <button type="button" className={`si-settings-btn${showSettings ? ' active' : ''}`} onClick={() => setShowSettings(v => !v)}><FeatherIcon icon="sliders" size={13} /> Columns</button>
            </Tooltip>
            {viewMode === 'modal' && canEdit && filters.property_id && filters.building_name && (
              <button type="button" className="si-btn si-btn--primary-solid si-add" onClick={() => openCreate()}>
                <FeatherIcon icon="plus" size={14} /> Add Entry
              </button>
            )}
          </div>
        </div>
        {showSettings && <ColWidthSettingsPanel colWidths={colWidths} onApply={applySettings} onClose={() => setShowSettings(false)} isApt={isApt} />}
        <Box sx={{ maxHeight: maxH, overflowY: 'auto', overflowX: 'auto', borderRadius: '0 0 10px 10px' }} className="si-scroll-box">
          {filters.property_id ? (
            <table className="si-tbl">
              <thead>
                <tr>
                  {cols.map(col => {
                    const w = cw(col.key);
                    return (
                      <th key={col.key} className={col.sticky ? 'si-sticky' : ''} style={{ width: w, minWidth: w }}>
                        <div className="th-inner">
                          {col.key !== 'expand' && col.key !== 'actions' && col.label}
                          {col.key !== 'expand' && col.key !== 'actions' && <ResizeHandle onResize={delta => adjustWidth(col.key, delta)} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 && (
                  <tr><td colSpan={cols.length} className="si-empty-cell">
                    <div className="si-empty">
                      <div className="si-empty__icon-wrap"><FeatherIcon icon="clipboard" size={26} color="#6366F1" /></div>
                      <Typography className="si-empty__title">No Units Found</Typography>
                      <Typography className="si-empty__sub">No units available for this property.</Typography>
                    </div>
                  </td></tr>
                )}
                {displayRows.map(row => {
                  if (viewMode === 'inline') {
                    if (row.isPending) {
                      return (
                        <InlinePendingCreateRow
                          key={`pending-${row.unit_number}`}
                          unitNumber={row.unit_number}
                          cols={cols}
                          colWidths={colWidths}
                          isApt={isApt}
                          unitOptions={unitOptions}
                          buildingType={buildingType}
                          onSave={handleInlineSave}
                          canEdit={canEdit}
                        />
                      );
                    }
                    return (
                      <InlineRow
                        key={row._id}
                        row={row}
                        cols={cols}
                        colWidths={colWidths}
                        isApt={isApt}
                        unitOptions={unitOptions}
                        buildingType={buildingType}
                        onSave={handleInlineSave}
                        onDelete={onDelete}
                        onOpenHistory={openHistory}
                        onOpenHistoric={openHistoric}
                        canEdit={canEdit}
                      />
                    );
                  }
                  const hasMultipleInspections = (row._allInspections?.length || 0) > 1;
                  const isDraftRow = row.is_draft === true;
                  return (
                    <tr
                      key={row.isPending ? `pending-${row.unit_number}` : row._id}
                      className={`si-row${row.isPending ? ' si-pending' : isDraftRow ? ' si-draft' : row.overall_status === 'has issues' ? ' si-issues' : ''}`}
                      onClick={() => canEdit && (row.isPending ? openCreate(row) : openEdit(row))}
                    >
                      {cols.map(col => {
                        const w = cw(col.key);
                        if (col.key === 'expand') {
                          return row.isPending
                            ? <td key="exp" className="si-expand-td" style={{ width: w }}><div className="si-expand-icon si-expand-icon--pending"><FeatherIcon icon="alert-circle" size={12} color="#DC2626" /></div></td>
                            : <td key="exp" className="si-expand-td" style={{ width: w }}>{canEdit && <div className="si-expand-icon si-expand-icon--view"><FeatherIcon icon="edit-3" size={12} color={isDraftRow ? '#D97706' : '#6366F1'} /></div>}</td>;
                        }
                        if (col.key === 'actions') {
                          return (
                            <td key="act" className="si-actions-td" style={{ width: w }} onClick={e => e.stopPropagation()}>
                              <div className="si-action-group">
                                {!row.isPending && <HistoryTriggerButton row={row} onOpen={openHistory} />}
                                {!row.isPending && hasMultipleInspections && (
                                  <Tooltip title={`View all ${row._allInspections.length} inspections`} arrow>
                                    <button type="button" className="si-ebtn" onClick={e => { e.stopPropagation(); openHistoric(row); }} style={{ background: '#EEF2FF', borderColor: '#6366F1', color: '#4F46E5', position: 'relative' }}>
                                      <FeatherIcon icon="archive" size={13} color="#4F46E5" />
                                      <span style={{ position: 'absolute', top: -5, right: -5, width: 14, height: 14, background: '#6366F1', color: 'white', borderRadius: '50%', fontSize: '8px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row._allInspections.length}</span>
                                    </button>
                                  </Tooltip>
                                )}
                                {canEdit && !row.isPending && (
                                  <Tooltip title={isDraftRow ? 'Complete draft' : 'Edit inspection'} arrow>
                                    <button type="button" className="si-ebtn" style={isDraftRow ? { borderColor: '#FCD34D', background: '#FFFBEB' } : {}} onClick={e => { e.stopPropagation(); openEdit(row); }}>
                                      <FeatherIcon icon={isDraftRow ? 'edit-3' : 'edit-2'} size={13} color={isDraftRow ? '#D97706' : '#6366F1'} />
                                    </button>
                                  </Tooltip>
                                )}
                                {canEdit && !row.isPending && (
                                  <Tooltip title="Delete" arrow>
                                    <button type="button" className="si-dbtn" onClick={e => { e.stopPropagation(); onDelete(row); }}>
                                      <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                                    </button>
                                  </Tooltip>
                                )}
                                {canEdit && row.isPending && (
                                  <Tooltip title="Start inspection" arrow>
                                    <button type="button" className="si-btn si-btn--inspect" onClick={e => { e.stopPropagation(); openCreate(row); }}>
                                      <FeatherIcon icon="plus" size={11} /> Inspect
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                          );
                        }
                        if (col.key === 'attachments') {
                          return row.isPending
                            ? <td key="att" className="si-cell-center" style={{ width: w }}><span className="si-cell-muted">—</span></td>
                            : <td key="att" className={col.sticky ? 'si-sticky' : ''} style={{ width: w }} onClick={e => e.stopPropagation()}><AttachmentViewCell row={row} /></td>;
                        }
                        if (row.isPending) {
                          if (col.key === 'unit_number') return <td key="unit" className="si-sticky si-pending-unit-td" style={{ width: w }}><span className="si-cell-unit si-cell-unit--pending">{row.unit_number}</span></td>;
                          if (col.key === 'inspection_date') return <td key="date" className="si-pending-date-td" style={{ width: w }}><span className="si-badge si-badge--not-inspected"><FeatherIcon icon="clock" size={9} /> Not Inspected</span></td>;
                          if (col.key === 'overall_status') return <td key="status" className="si-cell-center" style={{ width: w }}><span className="si-badge si-badge--pending-status"><FeatherIcon icon="minus-circle" size={9} /> Pending</span></td>;
                          return <td key={col.key} className="si-cell-center" style={{ width: w }}><span className="si-cell-muted si-cell-muted--faint">—</span></td>;
                        }
                        return (
                          <td key={col.key} className={col.sticky ? 'si-sticky' : ''} style={{ width: w }}>
                            <span className="si-cell-inner">{cellVal(row, col.key)}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {totals && displayRows.length > 0 && (
                  <tr className="si-summary">
                    <td colSpan={2} className="si-summary__label-cell"><span className="si-summary__label-inner"><FeatherIcon icon="list" size={12} color="#64748B" />{totals.total} Inspection{totals.total !== 1 ? 's' : ''}</span></td>
                    {cols.slice(2).map(col => {
                      if (col.key === 'overall_status') return <td key={col.key} className="si-summary__issues-cell"><span className="si-badge si-badge--issues"><FeatherIcon icon="alert-circle" size={10} /> {totals.issues} w/ issues</span></td>;
                      return <td key={col.key} className="si-summary__empty-cell"><span className="si-cell-muted">—</span></td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="si-empty">
              <div className="si-empty__icon-wrap"><FeatherIcon icon="home" size={42} color="#6366F1" /></div>
              <Typography className="si-empty__title">Select a Property to Start</Typography>
              <Typography className="si-empty__sub">Choose a property and building type from the selector above to view or add suite inspections.</Typography>
            </div>
          )}
        </Box>
      </Paper>
      <HistoryPopover
        inspectionId={historyPopover.inspectionId}
        unitNumber={historyPopover.unitNumber}
        anchorEl={historyPopover.anchorEl}
        onClose={closeHistory}
      />
      <InspectionFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        initialData={modalInitialData}
        buildingType={buildingType}
        unitOptions={unitOptions}
        onSave={handleModalSave}
      />
      <HistoricInspectionsModal
        isOpen={historicModalOpen}
        onClose={() => setHistoricModalOpen(false)}
        unitNumber={historicUnit.unitNumber}
        inspections={historicUnit.inspections}
        onEdit={openEdit}
        canEdit={canEdit}
      />
    </>
  );
};

export { SuiteInspectionListTable };